import { useParams, Link } from "react-router-dom";
import { motion } from "motion/react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimatedSection from "@/components/AnimatedSection";
import { getSolutionBySlug, getRelatedSolutions } from "@/mocks/solutions-detail";

export default function SolutionDetail() {
  const { slug } = useParams<{ slug: string }>();
  const solution = slug ? getSolutionBySlug(slug) : undefined;

  if (!solution) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900">Solution Not Found</h1>
          <p className="text-gray-500 mt-4 mb-8">The solution you are looking for does not exist.</p>
          <Link to="/solutions" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line" />
            Back to Solutions
          </Link>
        </section>
        <Footer />
      </div>
    );
  }

  const relatedSolutions = getRelatedSolutions(solution.id, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-20 bg-primary relative overflow-hidden" style={{ minHeight: "420px" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-accent/8 via-transparent to-primary" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/5 rounded-full blur-3xl" />
        <div className="relative w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-4xl mx-auto">
            <Link to="/solutions" className="inline-flex items-center gap-2 text-accent/80 hover:text-accent text-sm mb-6 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line" />
              All Solutions
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <i className={`${solution.features[0]?.icon || "ri-star-line"} text-accent text-2xl md:text-3xl`} />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">{solution.title}</h1>
            </div>
            <p className="text-white/80 text-base md:text-lg max-w-2xl leading-relaxed">{solution.heroDescription}</p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-14 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <AnimatedSection direction="left">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-1 bg-accent rounded-full" />
                  <span className="text-accent font-semibold text-sm uppercase tracking-wide">Overview</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                  Why {solution.title} Matters
                </h2>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">{solution.overview}</p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {solution.industries.map((ind) => (
                    <span key={ind} className="px-3 py-1.5 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20 whitespace-nowrap">
                      {ind}
                    </span>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection direction="right">
              <div className="rounded-2xl overflow-hidden border border-gray-100 aspect-[4/3] w-full">
                <img
                  src={solution.overviewImageUrl}
                  alt={solution.title}
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 md:py-20 bg-gray-50">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-1 bg-accent rounded-full" />
                <span className="text-accent font-semibold text-sm uppercase tracking-wide">Key Features</span>
                <div className="w-10 h-1 bg-accent rounded-full" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">What Makes It Powerful</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {solution.features.map((feature, index) => (
              <AnimatedSection key={feature.title} delay={index * 0.08}>
                <motion.div
                  whileHover={{ y: -5, transition: { duration: 0.25 } }}
                  className="p-6 rounded-xl bg-white border border-gray-100 hover:border-accent/30 transition-all duration-300 h-full"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center mb-4 flex-shrink-0">
                    <i className={`${feature.icon} text-accent text-lg`} />
                  </div>
                  <h3 className="text-base font-bold text-gray-800 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{feature.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 md:py-20 bg-primary relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-accent/3 rounded-full blur-3xl" />
        <div className="relative w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-1 bg-accent rounded-full" />
                <span className="text-accent font-semibold text-sm uppercase tracking-wide">Our Process</span>
                <div className="w-10 h-1 bg-accent rounded-full" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">How We Deliver {solution.title}</h2>
              <p className="text-gray-400 text-sm mt-3 max-w-xl mx-auto">A proven five-step methodology tailored for this solution.</p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {solution.howItWorks.map((step, index) => (
              <AnimatedSection key={step.step} delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -6, transition: { duration: 0.25 } }}
                  className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-accent/40 transition-all duration-300 text-center h-full"
                >
                  <div className="text-3xl font-bold text-accent/40 mb-3">{step.step}</div>
                  <h3 className="text-sm font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-xs text-white/70 leading-relaxed">{step.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-1 bg-accent rounded-full" />
                <span className="text-accent font-semibold text-sm uppercase tracking-wide">Benefits</span>
                <div className="w-10 h-1 bg-accent rounded-full" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Why Our Clients Choose This</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
            {solution.benefits.map((benefit, index) => (
              <AnimatedSection key={benefit.title} delay={index * 0.12}>
                <motion.div
                  whileHover={{ y: -4, transition: { duration: 0.25 } }}
                  className="flex gap-5 p-6 rounded-xl bg-white border border-gray-100 hover:border-accent/20 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center flex-shrink-0">
                    <i className={`${benefit.icon} text-accent text-xl`} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-800 mb-1">{benefit.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{benefit.description}</p>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Related Solutions */}
      {relatedSolutions.length > 0 && (
        <section className="py-14 md:py-20 bg-gray-50">
          <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-10 h-1 bg-accent rounded-full" />
                  <span className="text-accent font-semibold text-sm uppercase tracking-wide">Explore Related</span>
                  <div className="w-10 h-1 bg-accent rounded-full" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">You Might Also Be Interested In</h2>
              </div>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relatedSolutions.map((related, index) => (
                <AnimatedSection key={related.id} delay={index * 0.1}>
                  <Link to={`/solutions/${related.slug}`}>
                    <motion.div
                      whileHover={{ y: -6, transition: { duration: 0.25 } }}
                      className="p-6 rounded-xl bg-white border border-gray-100 hover:border-accent/30 transition-all duration-300 h-full cursor-pointer"
                    >
                      <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center mb-4 flex-shrink-0">
                        <i className={`${related.features[0]?.icon || "ri-star-line"} text-accent text-lg`} />
                      </div>
                      <h3 className="text-base font-bold text-gray-800 mb-2">{related.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">{related.tagline}</p>
                      <div className="flex items-center gap-1.5 text-accent text-sm font-medium mt-4 whitespace-nowrap">
                        Learn more
                        <i className="ri-arrow-right-line text-xs" />
                      </div>
                    </motion.div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-14 md:py-20 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/8 via-transparent to-primary" />
        <div className="absolute -top-32 -right-32 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />
        <div className="relative w-full px-4 md:px-8 lg:px-12 max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-5">{solution.ctaText}</h2>
            <p className="text-white/80 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Our team of experts is ready to design a {solution.title.toLowerCase()} solution tailored to your specific needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-8 py-3.5 bg-accent text-white font-semibold text-sm rounded-lg hover:bg-accent/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Get a Free Consultation
                </motion.button>
              </Link>
              <Link to="/solutions">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-8 py-3.5 bg-white/10 text-white font-semibold text-sm rounded-lg border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  View All Solutions
                </motion.button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}